<!-- Copyright: Seán I. O'Donoghue -->
<!-- The Settings header's name: the text is the edit button; editing opens a dark band with the
     first and last name (FieldPair, as the affiliation does), over the whole row -->

<template>
  <div class="row name">
    <button
      class="value"
      :class="{placeholder: !modelValue}"
      type="button"
      title="Update your name"
      @click="openEditor">
      {{ modelValue || "Add your name…" }}
    </button>
    <AppModalProfileEditLayer :is-open="isEditingName" direction="down">
      <AppModalProfileFieldPair
        ref="fieldsRef"
        v-model:first="nameDrafts.firstName"
        v-model:second="nameDrafts.lastName"
        :labels="['First Name', 'Last Name']"
        :autocomplete="['given-name', 'family-name']"
        :max-length="NAME_MAX_LENGTH"
        @commit="confirmName(user)"
        @tab="confirmName(user)"
      />
      <template #actions>
        <AppModalProfileEditButtons
          :can-cancel="isNameChanged"
          @cancel="discardName"
          @ok="confirmName(user)" />
      </template>
    </AppModalProfileEditLayer>
  </div>
</template>

<script setup>
import {computed, nextTick, ref} from "vue";
import AppModalProfileEditLayer from "@/components/App/Modal/Profile/EditLayer";
import AppModalProfileEditButtons from "@/components/App/Modal/Profile/EditButtons";
import AppModalProfileFieldPair from "@/components/App/Modal/Profile/FieldPair";
import useUserSafe from "@/composables/useUserSafe";
import {NAME_MAX_LENGTH, useNameEdit} from "@/composables/useNameEdit";
import {useDiscardOnOutside} from "@/composables/useProfileFieldEdits";

defineProps(["modelValue"]);

const {user} = useUserSafe();
const fieldsRef = ref(null);
// shared with the footer's OK and Cancel
const {isEditingName, nameDrafts, openName, discardName, confirmName} = useNameEdit();

// Cancel has something to discard only when a draft differs from the saved name
const isNameChanged = computed(() => {
  const {firstName = "", lastName = ""} = user.value || {};
  const isFirstChanged = nameDrafts.firstName.trim() !== (firstName || "");
  return isFirstChanged || nameDrafts.lastName.trim() !== (lastName || "");
});

async function openEditor() {
  openName(user.value);
  await nextTick();
  fieldsRef.value?.focus();
}

useDiscardOnOutside(() => isEditingName.value, discardName);
</script>

<style scoped>
.row {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0; /* a long name shrinks, keeping the homepage and LinkedIn icons in the row */
  max-width: 100%;
}
/* the Settings header: one font and colour for the name, affiliation and email; the name larger
   and bolder (800: the next weight index.html loads after 600) */
.name {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.3;
}
/* the text itself is the edit button (a button for the keyboard; global.css styles reset) */
.value {
  appearance: none;
  -webkit-appearance: none;
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  min-width: unset;
  font: inherit;
  color: inherit;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap; /* one line, cut with "…" (Seán, 2026-10-20; it wrapped) */
  text-overflow: ellipsis;
  cursor: pointer;
}
.value:hover {
  color: rgba(0, 0, 0, 0.65);
}
.value.placeholder {
  font-weight: 400;
  font-style: italic;
  color: rgba(0, 0, 0, 0.3);
}
.value.placeholder:hover {
  color: rgba(0, 0, 0, 0.5); /* "Add name", as a filled name's hover (DESIGN.md § 15) */
}
</style>
